/**
 * Task Intelligence Dashboard — R138p · Feb 2026
 *
 * A single-page cross-project analytics workbench. Ten sections:
 *   1. Global filter bar (sticky) — date range, project, client, brand, assignee, priority, tag
 *   2. KPI strip — 8 tiles with current vs previous period delta
 *   3. Trend chart — created vs done + on-time% + variance% lines
 *   4. Leaderboard table — sortable, drilldown per assignee
 *   5. Variance heatmap — projects × people
 *   6. Timeliness stacked bars — by assignee or project
 *   7. Overrun attribution table — who caused the most overrun
 *   8. Free/busy capacity panel — planned load vs capacity
 *   9. Costly / profitable / unprofitable task tables
 *  10. AI narrative — Claude-generated insights (on-demand)
 *
 * Every metric card and table row exposes a "drilldown" slide-over so the
 * underlying task ids can be inspected — no unverifiable performance claims.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, CartesianGrid, ReferenceLine, AreaChart, Area,
} from "recharts";
import {
  ArrowUpRight, ArrowDownRight, Minus, Sparkles, RefreshCw, X,
  TrendingUp, TrendingDown, Clock, AlertTriangle, DollarSign, Users,
  Activity, Layers, Save, Trash2, Bookmark, Download, GitCompareArrows,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { PersonChip } from "@/components/PersonChip";
import Tasks, { TaskDetail } from "@/pages/app/Tasks";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiErrorMessage } from "@/lib/apiError";

/* ─────────── helpers ─────────── */
const fmtPct = (v) => v == null ? "—" : `${Number(v).toFixed(1)}%`;
const fmtNum = (v, d = 1) => v == null ? "—" : Number(v).toFixed(d);
const fmtHrs = (v) => v == null ? "—" : `${Number(v).toFixed(1)}h`;
const toISO = (d) => new Date(d).toISOString().slice(0, 10);
const daysAgoISO = (n) => toISO(Date.now() - n * 86400000);

/* Colour scale for variance / delta values — negative = good (under budget) */
const varianceCls = (v) => {
  if (v == null) return "text-muted-foreground";
  if (v <= -10) return "text-emerald-600 dark:text-emerald-400";
  if (v <= 10)  return "text-foreground/80";
  if (v <= 30)  return "text-amber-600 dark:text-amber-400";
  return "text-destructive";
};
const deltaCls = (v, higherIsBetter = true) => {
  if (v == null || v === 0) return "text-muted-foreground";
  const good = higherIsBetter ? v > 0 : v < 0;
  return good ? "text-emerald-600 dark:text-emerald-400" : "text-destructive";
};

/* ─────────── KPI tile ─────────── */
function Sparkline({ data, dataKey, height = 34, color = "currentColor" }) {
  const values = (data || []).map((d) => d[dataKey]).filter((v) => v != null && !Number.isNaN(v));
  if (values.length < 2) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 opacity-25" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`ti-spark-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} fill={`url(#ti-spark-${dataKey})`} dot={false} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiTile({ label, value, prevValue, delta, higherIsBetter = true, hint, testid, compare, spark }) {
  const arrow = delta == null || delta === 0 ? Minus
    : (delta > 0 ? ArrowUpRight : ArrowDownRight);
  const Arrow = arrow;
  return (
    <div className="relative border border-border p-5 bg-background hover:border-primary/40 transition overflow-hidden" data-testid={testid}>
      {spark && <Sparkline data={spark.data} dataKey={spark.dataKey} color={spark.color || "#6366f1"} />}
      <div className="relative">
        <div className="label-eyebrow mb-2 text-[10px]">{label}</div>
      {compare ? (
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="text-3xl font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
          <div className="text-xs text-muted-foreground">
            <span className="inline-block mr-1 uppercase tracking-widest text-[9px]">prev</span>
            <span className="text-sm text-foreground/70">{prevValue}</span>
          </div>
        </div>
      ) : (
        <div className="text-3xl font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      )}
      <div className={`text-xs mt-2 inline-flex items-center gap-1 ${deltaCls(delta, higherIsBetter)}`}>
        <Arrow className="w-3 h-3" />
        {delta == null ? "—" : (delta > 0 ? "+" : "") + Number(delta).toFixed(1)}
        <span className="text-muted-foreground ml-1">vs prev</span>
      </div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
      </div>
    </div>
  );
}

/* ─────────── Drilldown slide-over ─────────── */
/* ─────────── Person Tasks dialog · R138p-p7 ───────────
   Instead of a lean sheet with a list of task ids, we now embed the
   FULL Tasks module inside a wide dialog — preseeded with the target
   assignee filter. Managers get the entire Tasks toolset (List / Kanban
   / Cards / Calendar / Gantt / Feed / Dashboard views, bulk-edit,
   quick-add, filters, subtask kanban, deep-linked task details) but
   scoped to one person, without leaving the intelligence board. */
function CapacityOverridesDialog({ wsId, members, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    user_id: "",
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date(Date.now() + 6 * 86400000).toISOString().slice(0, 10),
    hours_per_day: 4,
    kind: "part-time",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/capacity-overrides`);
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load overrides")); }
    finally { setLoading(false); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.user_id) return toast.error("Pick a person");
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/capacity-overrides`, {
        user_id: form.user_id,
        from_date: form.from_date,
        to_date: form.to_date,
        hours_per_day: Number(form.hours_per_day),
        kind: form.kind,
        reason: form.reason?.trim() || null,
      });
      toast.success("Override added");
      setShowAdd(false);
      setForm({ ...form, reason: "" });
      await load(); onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save")); }
    finally { setBusy(false); }
  };
  const del = async (oid) => {
    if (!window.confirm("Delete this capacity override?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/capacity-overrides/${oid}`);
      toast.success("Deleted");
      await load(); onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="ti-capacity-overrides-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            Capacity overrides
            <span className="text-[10px] text-muted-foreground font-normal">— per-person, per-period</span>
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-muted-foreground -mt-2 mb-2">
          Overrides beat the shift-based default for the specified date range.
          Use for sabbaticals, part-time weeks, sprint crunches or off-cycle
          onboarding without touching HR settings.
        </div>
        <div className="flex justify-end mb-2">
          <Button size="sm" className="rounded-none h-8" onClick={() => setShowAdd((x) => !x)}
                  data-testid="ti-capacity-overrides-add-btn">
            {showAdd ? "Cancel" : "+ Add override"}
          </Button>
        </div>
        {showAdd && (
          <div className="border border-primary/40 bg-primary/5 p-3 mb-3 space-y-2" data-testid="ti-capacity-overrides-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label-eyebrow text-[10px] mb-1">Person</div>
                <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid="ti-override-user"><SelectValue placeholder="Pick person" /></SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                        {m.name || m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-eyebrow text-[10px] mb-1">Kind</div>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid="ti-override-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sabbatical">Sabbatical (0h/day)</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="sprint-crunch">Sprint crunch</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-eyebrow text-[10px] mb-1">From</div>
                <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="h-9 rounded-none" data-testid="ti-override-from" />
              </div>
              <div>
                <div className="label-eyebrow text-[10px] mb-1">To</div>
                <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="h-9 rounded-none" data-testid="ti-override-to" />
              </div>
              <div>
                <div className="label-eyebrow text-[10px] mb-1">Hours / day</div>
                <Input type="number" min={0} max={24} step={0.5} value={form.hours_per_day}
                       onChange={(e) => setForm({ ...form, hours_per_day: e.target.value })}
                       className="h-9 rounded-none" data-testid="ti-override-hours" />
              </div>
              <div>
                <div className="label-eyebrow text-[10px] mb-1">Reason (optional)</div>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                       className="h-9 rounded-none" placeholder="Client sabbatical, family leave..."
                       data-testid="ti-override-reason" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" className="rounded-none" onClick={save} disabled={busy} data-testid="ti-override-save">
                {busy ? "Saving…" : "Save override"}
              </Button>
            </div>
          </div>
        )}
        <div className="max-h-[50vh] overflow-y-auto border border-border" data-testid="ti-capacity-overrides-list">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground text-xs">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-xs">No overrides yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="border-b border-border bg-muted/40">
                <tr>
                  {["Person","Kind","From","To","Hrs/day","Reason",""].map((h) =>
                    <th key={h} className="text-left px-2 py-2 label-eyebrow whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="border-b border-border/50" data-testid={`ti-override-row-${o.id}`}>
                    <td className="px-2 py-1.5 font-semibold">{o.user_name || o.user_id.slice(0, 8)}</td>
                    <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 bg-muted text-[10px] uppercase tracking-wider">{o.kind}</span></td>
                    <td className="px-2 py-1.5 font-mono">{o.from_date}</td>
                    <td className="px-2 py-1.5 font-mono">{o.to_date}</td>
                    <td className="px-2 py-1.5 font-mono">{o.hours_per_day}h</td>
                    <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[200px]" title={o.reason || ""}>{o.reason || "—"}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => del(o.id)} className="text-destructive hover:text-destructive/80 text-[10px] uppercase tracking-wider" data-testid={`ti-override-del-${o.id}`}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PersonTasksDialog({ open, onClose, personName, personId, projectId, projectName, taskIds }) {  const title = projectName
    ? `${projectName} · project tasks`
    : personName
      ? `${personName}'s tasks${taskIds?.length ? ` (${taskIds.length} replanned)` : ""}`
      : "Tasks";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-[96vw] w-[96vw] h-[92vh] p-0 gap-0 overflow-hidden flex flex-col"
        data-testid="ti-person-tasks-dialog"
      >
        <DialogHeader className="px-4 sm:px-6 py-3 border-b border-border flex-shrink-0">
          <DialogTitle className="text-base flex items-center gap-2">
            <span className="label-eyebrow text-[10px]">Drilldown</span>
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto" data-testid="ti-person-tasks-body">
          {open && (
            <Tasks
              embedded
              initialAssigneeIds={personId ? [personId] : null}
              initialProjectIds={projectId ? [projectId] : null}
              initialTaskIds={taskIds && taskIds.length ? taskIds : null}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────── Filter bar ─────────── */

// R138ad · Feb 2026 — Reusable collapsible section wrapper for the
// heavy Task Intelligence report cards. Open/closed state persists per
// section-key so directors keep their layout across visits.
function TISection({ storageKey, title, testid, children, defaultOpen = true, right = null }) {
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(`ti-section:${storageKey}`); return v == null ? defaultOpen : v === "1"; }
    catch { return defaultOpen; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(`ti-section:${storageKey}`, next ? "1" : "0"); } catch { /* ignore */ }
  };
  return (
    <div className="border border-border" data-testid={testid}>
      <button type="button" onClick={toggle}
                className="w-full flex items-center justify-between p-2 sm:p-3 hover:bg-muted/30"
                data-testid={`${testid}-toggle`}>
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="text-xs sm:text-sm font-semibold">{title}</span>
        </div>
        {right && <div className="text-[10px] text-muted-foreground hidden sm:block">{right}</div>}
      </button>
      {open && <div className="p-3 sm:p-5 pt-2 border-t border-border">{children}</div>}
    </div>
  );
}

function FilterBar({ filters, setFilters, options, presets, onSavePreset, onDeletePreset, onApplyPreset, compare, setCompare, onExportPdf }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v || undefined }));
  // R138ad · Feb 2026 — Filter bar collapses by default on mobile so the
  // KPI strip lands above the fold. On desktop stays open by default.
  // Explicit user preference (persisted per browser) always wins.
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("ti-filter-open");
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch { /* ignore */ }
    // First visit: default = open on desktop (>=640px), closed on mobile
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(min-width: 640px)").matches;
    }
    return true;
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem("ti-filter-open", next ? "1" : "0"); } catch { /* ignore */ }
  };
  const activeCount = Object.values(filters).filter(Boolean).length;
  return (
    <div className="border border-border bg-muted/30 ti-filter-bar" data-testid="ti-filter-bar">
      <button type="button" onClick={toggle}
                className="flex w-full items-center justify-between gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs font-semibold hover:bg-muted/60 transition-colors"
                data-testid="ti-filter-toggle"
                aria-expanded={open}>
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="uppercase tracking-wide">Filters</span>
          {activeCount > 0 && (
            <span className="ml-1 inline-flex items-center h-5 px-1.5 rounded-none bg-primary text-primary-foreground text-[10px] font-bold">
              {activeCount} active
            </span>
          )}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {open ? "Tap to hide" : "Tap to show"}
        </span>
      </button>
      <div className={open ? "block px-2 sm:px-4 pb-2 sm:pb-4" : "hidden"}>
      {/* Row 1: from/to + quick range presets — presets get their own row on mobile */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-end">
        <div className="grid grid-cols-2 gap-2 sm:contents">
          <div>
            <div className="label-eyebrow mb-1 text-[10px]">From</div>
            <Input type="date" value={filters.from || ""} onChange={(e) => set("from", e.target.value)} className="h-9 rounded-none w-full sm:w-auto" data-testid="ti-filter-from" />
          </div>
          <div>
            <div className="label-eyebrow mb-1 text-[10px]">To</div>
            <Input type="date" value={filters.to || ""} onChange={(e) => set("to", e.target.value)} className="h-9 rounded-none w-full sm:w-auto" data-testid="ti-filter-to" />
          </div>
        </div>
        <QuickRange setFilters={setFilters} />
      </div>
      {/* Row 2: entity filters — grid 2 col on mobile, natural flex-wrap on desktop */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:items-end mt-3">
        <FilterSelect label="Project"   value={filters.project_id}    onChange={(v)=>set("project_id",v)}   options={options.projects}    testid="ti-filter-project" />
        <FilterSelect label="Client"    value={filters.client_id}     onChange={(v)=>set("client_id",v)}    options={options.clients}     testid="ti-filter-client" />
        <FilterSelect label="Brand"     value={filters.brand_id}      onChange={(v)=>set("brand_id",v)}     options={options.brands}      testid="ti-filter-brand" />
        <FilterSelect label="Assignee"  value={filters.assignee_id}   onChange={(v)=>set("assignee_id",v)}  options={options.members}     testid="ti-filter-assignee" />
        <FilterSelect label="Priority"  value={filters.priority}      onChange={(v)=>set("priority",v)}     options={["urgent","high","medium","low"].map(p=>({value:p,label:p}))} testid="ti-filter-priority" />
        <FilterSelect label="Tag"       value={filters.tag}           onChange={(v)=>set("tag",v)}          options={options.tags}        testid="ti-filter-tag" />
        <FilterSelect label="Dept"      value={filters.department_id} onChange={(v)=>set("department_id",v)} options={options.departments} testid="ti-filter-dept" />
        {/* R138bu · Hide idle members — leaders can toggle idle rows on/off
            without changing the date window. Persists in localStorage. */}
        <label className="flex items-center gap-1.5 h-9 px-2 border border-border cursor-pointer hover:bg-muted text-[10px] uppercase tracking-widest col-span-2 sm:col-span-1"
                title="Hide members with zero tasks in this window"
                data-testid="ti-filter-hide-idle-wrap">
          <input
            type="checkbox"
            checked={!!filters.hide_idle}
            onChange={(e) => set("hide_idle", e.target.checked || undefined)}
            data-testid="ti-filter-hide-idle"
          />
          Hide idle
        </label>
        {(Object.values(filters).some(Boolean)) && (
          <Button variant="outline" size="sm" className="h-9 rounded-none col-span-2 sm:col-span-1" onClick={()=>setFilters({from:daysAgoISO(30),to:toISO(Date.now())})} data-testid="ti-filter-reset">
            <X className="w-3 h-3 mr-1" /> Reset
          </Button>
        )}
      </div>
      {/* Row 3: presets · compare · export */}
      <div className="flex flex-wrap gap-2 items-end mt-3 pt-3 border-t border-border/60 ti-toolbar-print-hide">
        <PresetsDropdown presets={presets} onApplyPreset={onApplyPreset} onDeletePreset={onDeletePreset} />
        <SavePresetInline filters={filters} onSave={onSavePreset} />
        <div className="flex-1" />
        <Button size="sm" variant={compare ? "default" : "outline"} className="h-9 rounded-none" onClick={() => setCompare(!compare)} data-testid="ti-compare-toggle">
          <GitCompareArrows className="w-3.5 h-3.5 mr-1" />
          <span className="hidden sm:inline">{compare ? "Comparing" : "Compare vs prev"}</span>
          <span className="sm:hidden">{compare ? "vs prev" : "Compare"}</span>
        </Button>
        <Button size="sm" variant="outline" className="h-9 rounded-none" onClick={onExportPdf} data-testid="ti-export-pdf">
          <Download className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Export PDF</span>
        </Button>
      </div>
      </div>
    </div>
  );
}

function PresetsDropdown({ presets, onApplyPreset, onDeletePreset }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button size="sm" variant="outline" className="h-9 rounded-none" onClick={() => setOpen((o) => !o)} data-testid="ti-presets-dropdown">
        <Bookmark className="w-3.5 h-3.5 mr-1" /> Presets ({presets.length})
      </Button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 border border-border bg-background z-20 max-h-80 overflow-y-auto shadow-lg" data-testid="ti-presets-panel">
          {presets.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground">No saved presets yet.</div>
          ) : presets.map((p) => (
            <div key={p.id} className="flex justify-between items-center px-3 py-2 border-b border-border/40 hover:bg-muted/30" data-testid={`ti-preset-row-${p.id}`}>
              <button className="text-left flex-1 min-w-0" onClick={() => { onApplyPreset(p); setOpen(false); }}>
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {Object.keys(p.filters || {}).length} filters{p.shared ? " · shared" : ""}
                  {!p.owned ? ` · by ${p.created_by_name || "someone"}` : ""}
                </div>
              </button>
              {p.owned && (
                <button className="ml-2 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); if (confirm(`Delete preset "${p.name}"?`)) onDeletePreset(p.id); }} data-testid={`ti-preset-del-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SavePresetInline({ filters, onSave }) {
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const save = () => {
    if (!name.trim()) { toast.error("Give the preset a name."); return; }
    onSave({ name: name.trim(), filters, shared });
    setName("");
  };
  return (
    <div className="flex gap-1 items-end">
      <div>
        <div className="label-eyebrow mb-1 text-[10px]">Save current as</div>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Weekly review"
          className="h-9 rounded-none w-52"
          data-testid="ti-preset-name"
          onKeyDown={(e) => { if (e.key === "Enter") save(); }}
        />
      </div>
      <label className="text-xs text-muted-foreground h-9 inline-flex items-center gap-1 px-2 border border-border select-none cursor-pointer">
        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="ti-preset-shared" />
        Share
      </label>
      <Button size="sm" className="h-9 rounded-none" onClick={save} data-testid="ti-preset-save">
        <Save className="w-3.5 h-3.5 mr-1" /> Save
      </Button>
    </div>
  );
}

function QuickRange({ setFilters }) {
  // R138ag · Feb 2026 — Calendar / fiscal quarter presets.
  // Persist the fiscal toggle so power-users don't re-pick each visit.
  const [fiscal, setFiscal] = useState(() => {
    try { return localStorage.getItem("ti-quarter-mode") === "fiscal"; }
    catch { return false; }
  });
  const toggleFiscal = () => {
    const next = !fiscal;
    setFiscal(next);
    try { localStorage.setItem("ti-quarter-mode", next ? "fiscal" : "calendar"); } catch { /* ignore */ }
  };
  const applyRange = (fromDate, toDate) => {
    setFilters((f) => ({ ...f, from: toISO(fromDate), to: toISO(toDate) }));
  };
  const setDays = (n) => applyRange(Date.now() - n * 86400000, Date.now());

  // Month presets (past/this/next)
  const monthRange = (offset) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return [start, end];
  };
  // R148g · Feb 2026 — Week presets (past/this/next). Weeks start Monday
  // to match ISO conventions used across the app.
  const weekRange = (offset) => {
    const now = new Date();
    const day = now.getDay();                            // 0 (Sun) .. 6 (Sat)
    const diffToMon = (day === 0 ? -6 : 1 - day);        // shift so Mon = 0
    const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon + offset * 7);
    const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
    return [mon, sun];
  };
  // Quarter presets — pass fiscalStartMonth: 0 for calendar (Jan), 3 for fiscal (Apr)
  const quarterRange = (offset) => {
    const now = new Date();
    const startMonth = fiscal ? 3 : 0;
    // months since anchor
    const monthsFromAnchor = (now.getFullYear() * 12 + now.getMonth()) - startMonth;
    const currentQIdx = Math.floor(monthsFromAnchor / 3) + offset;
    const qStartMonthAbsolute = startMonth + currentQIdx * 3;
    const start = new Date(Math.floor(qStartMonthAbsolute / 12), qStartMonthAbsolute % 12, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
    return [start, end];
  };
  // Year presets — calendar always Jan–Dec; fiscal Apr–Mar
  const yearRange = (offset) => {
    const now = new Date();
    if (fiscal) {
      // Determine current fiscal year start (Apr 1)
      const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      const start = new Date(fyStartYear + offset, 3, 1);
      const end = new Date(fyStartYear + offset + 1, 2, 31);
      return [start, end];
    }
    const start = new Date(now.getFullYear() + offset, 0, 1);
    const end = new Date(now.getFullYear() + offset, 11, 31);
    return [start, end];
  };

  const chip = (label, onClick, testid) => (
    <Button key={label} type="button" size="sm" variant="outline"
            className="h-8 rounded-none px-2 text-[11px] uppercase tracking-wide"
            onClick={onClick} data-testid={testid}>
      {label}
    </Button>
  );

  return (
    <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-1" data-testid="ti-quick-range">
      <div className="flex flex-wrap gap-1 items-center">
        {[[7,"7d"],[30,"30d"],[90,"90d"],[180,"6m"]].map(([n,l]) =>
          chip(l, () => setDays(n), `ti-qr-${l}`))}
        <span className="h-6 w-px bg-border mx-1 hidden sm:inline-block" />
        {chip("Past wk", () => applyRange(...weekRange(-1)), "ti-qr-past-week")}
        {chip("This wk", () => applyRange(...weekRange(0)), "ti-qr-this-week")}
        {chip("Next wk", () => applyRange(...weekRange(1)), "ti-qr-next-week")}
        <span className="h-6 w-px bg-border mx-1 hidden sm:inline-block" />
        {chip("Past mo", () => applyRange(...monthRange(-1)), "ti-qr-past-month")}
        {chip("This mo", () => applyRange(...monthRange(0)), "ti-qr-this-month")}
        {chip("Next mo", () => applyRange(...monthRange(1)), "ti-qr-next-month")}
        <span className="h-6 w-px bg-border mx-1 hidden sm:inline-block" />
        {chip("Past Q", () => applyRange(...quarterRange(-1)), "ti-qr-past-quarter")}
        {chip("This Q", () => applyRange(...quarterRange(0)), "ti-qr-this-quarter")}
        {chip("Next Q", () => applyRange(...quarterRange(1)), "ti-qr-next-quarter")}
        <span className="h-6 w-px bg-border mx-1 hidden sm:inline-block" />
        {chip("Past Yr", () => applyRange(...yearRange(-1)), "ti-qr-past-year")}
        {chip("This Yr", () => applyRange(...yearRange(0)), "ti-qr-this-year")}
        {chip("Next Yr", () => applyRange(...yearRange(1)), "ti-qr-next-year")}
        <button type="button" onClick={toggleFiscal}
                title={`Quarter/Year presets · currently ${fiscal ? "Fiscal (Apr–Mar)" : "Calendar (Jan–Dec)"}. Click to switch.`}
                className={`h-8 px-2 text-[10px] uppercase tracking-wide border ${fiscal ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:bg-muted"}`}
                data-testid="ti-qr-fiscal-toggle">
          {fiscal ? "FY" : "CY"}
        </button>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="min-w-0 sm:min-w-[140px] w-full sm:w-auto">
      <div className="label-eyebrow mb-1 text-[10px]">{label}</div>
      <Select value={value || "_all_"} onValueChange={(v) => onChange(v === "_all_" ? undefined : v)}>
        <SelectTrigger className="h-9 rounded-none text-xs w-full" data-testid={testid}>
          <SelectValue placeholder={`All ${label.toLowerCase()}s`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all_">All</SelectItem>
          {(options || []).map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ─────────── Main page ─────────── */
export default function TaskIntelligence() {
  const { activeWorkspaceId } = useAuth();
  const ws = activeWorkspaceId;
  // R138p-p9 · Filter state <-> URL sync. On mount we hydrate from
  // ?from=&to=&project_id=&… so managers can share a link that lands
  // on the exact same view; on every filter change we push the new
  // params via history.replaceState so navigating away/back preserves
  // the slice too.
  const [urlParams, setUrlParams] = useSearchParams();
  const _FKEYS = ["from","to","project_id","client_id","brand_id","assignee_id","collaborator_id","priority","tag","department_id","status"];
  const _initFilters = useMemo(() => {
    const f = { from: urlParams.get("from") || daysAgoISO(30), to: urlParams.get("to") || toISO(Date.now()) };
    _FKEYS.forEach((k) => { if (k !== "from" && k !== "to") { const v = urlParams.get(k); if (v) f[k] = v; } });
    return f;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filters, setFilters] = useState(_initFilters);
  // Sync filters -> URL (replace so back-button works)
  const _syncRef = useRef(false);
  useEffect(() => {
    if (!_syncRef.current) { _syncRef.current = true; return; }
    const p = new URLSearchParams();
    _FKEYS.forEach((k) => { const v = filters[k]; if (v) p.append(k, v); });
    setUrlParams(p, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const [options, setOptions] = useState({ projects: [], clients: [], brands: [], members: [], tags: [], departments: [] });
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [timeliness, setTimeliness] = useState(null);
  const [costly, setCostly] = useState(null);
  const [overrun, setOverrun] = useState(null);
  const [freeBusy, setFreeBusy] = useState(null);
  const [creators, setCreators] = useState(null);
  const [projectEconomics, setProjectEconomics] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [drill, setDrill] = useState({ open: false, personName: "", personId: null, projectId: null, projectName: "", taskIds: null });
  const [editTask, setEditTask] = useState({ open: false, task: null, onSaved: null });
  const [rawMembers, setRawMembers] = useState([]);
  const [timelinessBy, setTimelinessBy] = useState("assignee");
  const [trendsBucket, setTrendsBucket] = useState("day");
  const [compare, setCompare] = useState(false);
  const [prevTrends, setPrevTrends] = useState(null);
  const [prevFreeBusy, setPrevFreeBusy] = useState(null);  // R138ak — team load compare
  const [showCapacityOverrides, setShowCapacityOverrides] = useState(false);  // R138aj
  const [presets, setPresets] = useState([]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  }, [filters]);

  const load = useCallback(async () => {
    if (!ws) return;
    const base = `/workspaces/${ws}/task-intelligence`;
    const [s, t, lb, mx, tl, cp, ov, fb, cr, pe] = await Promise.all([
      api.get(`${base}/summary?${qs}`).catch(() => ({data: null})),
      api.get(`${base}/trends?bucket=${trendsBucket}&${qs}`).catch(() => ({data: null})),
      api.get(`${base}/leaderboard?${qs}`).catch(() => ({data: null})),
      api.get(`${base}/variance-matrix?${qs}`).catch(() => ({data: null})),
      api.get(`${base}/timeliness?by=${timelinessBy}&${qs}`).catch(() => ({data: null})),
      api.get(`${base}/costly-profitable?${qs}`).catch(() => ({data: null})),
      api.get(`${base}/overrun-attribution?${qs}`).catch(() => ({data: null})),
      api.get(`${base}/free-busy?use_period=true&${qs}`).catch(() => ({data: null})),
      api.get(`${base}/creators?${qs}`).catch(() => ({data: null})),
      api.get(`${base}/project-economics?${qs}`).catch(() => ({data: null})),
    ]);
    setSummary(s.data); setTrends(t.data); setLeaderboard(lb.data);
    setMatrix(mx.data); setTimeliness(tl.data); setCostly(cp.data);
    setOverrun(ov.data); setFreeBusy(fb.data);
    setCreators(cr.data); setProjectEconomics(pe.data);
  }, [ws, qs, timelinessBy, trendsBucket]);

  useEffect(() => { load(); }, [load]);

  // Compare-vs-prev: fetch trends AND free-busy for the previous
  // period. Free-busy comparison powers the "Team Load Compare" pill
  // on each user's Free/Busy card (R138ak).
  useEffect(() => {
    if (!compare || !ws) { setPrevTrends(null); setPrevFreeBusy(null); return; }
    const from = new Date(filters.from || daysAgoISO(30));
    const to = new Date(filters.to || Date.now());
    const span = to.getTime() - from.getTime();
    const prevTo = new Date(from.getTime());
    const prevFrom = new Date(from.getTime() - span);
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && k !== "from" && k !== "to") p.append(k, v); });
    p.append("from", toISO(prevFrom));
    p.append("to",   toISO(prevTo));
    api.get(`/workspaces/${ws}/task-intelligence/trends?bucket=${trendsBucket}&${p.toString()}`)
       .then((r) => setPrevTrends(r.data))
       .catch(() => setPrevTrends(null));
    api.get(`/workspaces/${ws}/task-intelligence/free-busy?use_period=true&${p.toString()}`)
       .then((r) => setPrevFreeBusy(r.data))
       .catch(() => setPrevFreeBusy(null));
  }, [compare, ws, filters, trendsBucket]);

  // Compare-mode trend chart data — pair current and previous buckets by
  // ordinal position so both series can be shown on a single X axis.
  const trendChartData = useMemo(() => {
    const cur = trends?.series || [];
    if (!compare || !prevTrends?.series?.length) return cur;
    const prev = prevTrends.series;
    const len = Math.max(cur.length, prev.length);
    const out = [];
    for (let i = 0; i < len; i++) {
      const c = cur[i] || {};
      const p = prev[i] || {};
      out.push({
        bucket: c.bucket || p.bucket || `#${i + 1}`,
        created: c.created, done: c.done, on_time_pct: c.on_time_pct, variance_pct: c.variance_pct,
        prev_created: p.created, prev_done: p.done, prev_on_time_pct: p.on_time_pct, prev_variance_pct: p.variance_pct,
      });
    }
    return out;
  }, [trends, prevTrends, compare]);

  // Load filter presets
  const loadPresets = useCallback(async () => {
    if (!ws) return;
    try {
      const r = await api.get(`/workspaces/${ws}/task-intelligence/presets`);
      setPresets(r.data.presets || []);
    } catch (e) { /* ignore */ }
  }, [ws]);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const savePreset = async ({ name, filters: f, shared }) => {
    try {
      await api.post(`/workspaces/${ws}/task-intelligence/presets`, { name, filters: f, shared });
      toast.success(`Preset "${name}" saved`);
      loadPresets();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save preset."));
    }
  };
  const deletePreset = async (id) => {
    try {
      await api.delete(`/workspaces/${ws}/task-intelligence/presets/${id}`);
      toast.success("Preset deleted");
      loadPresets();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not delete preset."));
    }
  };
  const applyPreset = (p) => {
    setFilters({ ...(p.filters || {}) });
    toast.success(`Applied "${p.name}"`);
  };

  const exportPdf = () => {
    // Print-friendly export: adds a `body.ti-printing` class the print CSS
    // uses to hide the app shell, sidebar and other chrome; then invokes
    // window.print(). Users pick "Save as PDF" in the OS print dialog —
    // works on every platform with zero client-side deps.
    document.body.classList.add("ti-printing");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("ti-printing"), 500);
    }, 100);
  };

  // Load filter options once
  useEffect(() => {
    if (!ws) return;
    Promise.all([
      api.get(`/workspaces/${ws}/projects?limit=200`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${ws}/clients?limit=200`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${ws}/brands?limit=200`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${ws}/members`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${ws}/departments`).catch(() => ({ data: [] })),
    ]).then(([p, c, b, m, d]) => {
      const norm = (arr, lb = "name") => (Array.isArray(arr) ? arr : (arr?.items || arr?.projects || arr?.rows || []))
        .map((x) => ({ value: x.id, label: x[lb] || x.name || x.email || x.title || "—" }));
      const membersList = (Array.isArray(m.data) ? m.data : m.data?.items || []);
      setRawMembers(membersList);
      setOptions({
        projects: norm(p.data),
        clients: norm(c.data),
        brands: norm(b.data),
        members: membersList.map((u) => ({
          value: u.user_id || u.id, label: u.name || u.email || "—",
        })),
        departments: norm(d.data),
        tags: [],
      });
    });
  }, [ws]);

  const runNarrative = async (tone = "executive") => {
    if (!ws) return;
    setNarrativeBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${ws}/task-intelligence/ai-narrative?${qs}`,
        { tone },
      );
      setNarrative(r.data);
    } catch (e) {
      setNarrative({ narrative: { headline: "AI is not available right now.", wins: [], concerns: [], person_watchlist: [], recommended_actions: [] } });
    } finally {
      setNarrativeBusy(false);
    }
  };

  const openDrill = (personName, personId) => setDrill({ open: true, personName, personId, projectId: null, projectName: "", taskIds: null });
  const openProjectDrill = (projectName, projectId) => setDrill({ open: true, personName: "", personId: null, projectId, projectName, taskIds: null });
  const openTaskIdsDrill = (personName, personId, taskIds) => setDrill({ open: true, personName, personId, projectId: null, projectName: "", taskIds });
  // R138p-p6 · Open the FULL Task Detail modal (same one used inside the
  // Tasks module) — fetch the fresh doc first so all fields are available.
  const openEdit = async (task, onSaved) => {
    if (!task?.id) return;
    setEditTask({ open: true, task, onSaved });   // optimistic
    try {
      const { data } = await api.get(`/tasks/${task.id}`);
      if (data && data.id === task.id) {
        setEditTask((cur) => (cur?.task?.id === task.id ? { ...cur, task: { ...cur.task, ...data } } : cur));
      }
    } catch { /* fall back to the lean row */ }
  };
  const patchEditingTask = async (patch) => {
    if (!editTask.task?.id) return;
    try {
      const { data } = await api.patch(`/tasks/${editTask.task.id}`, patch);
      setEditTask((cur) => (cur?.task?.id === editTask.task.id ? { ...cur, task: { ...cur.task, ...data } } : cur));
      // Notify the caller so the drill drawer + board metrics refresh
      editTask.onSaved && editTask.onSaved();
      load();
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save task."));
    }
  };

  const kpis = summary?.kpis || {};
  const kpi = (k) => kpis[k]?.current;
  const kpiPrev = (k) => kpis[k]?.previous;
  const kpiDelta = (k) => kpis[k]?.delta;
  const sparkSeries = trends?.series || [];

  return (
    <div className="space-y-4 sm:space-y-6 px-3 sm:px-6 md:px-10 pb-6" data-testid="task-intelligence-page">
      <PageHeader
        eyebrow="Analytics"
        title="Task Intelligence"
        description="One dashboard. Every project. Every person. Every hour. Filter, drill and verify."
        actions={
          <Button className="rounded-none h-9" onClick={() => load()} data-testid="ti-refresh">
            <RefreshCw className="w-3 h-3 mr-2" /> Refresh
          </Button>
        }
      />

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        options={options}
        presets={presets}
        onSavePreset={savePreset}
        onDeletePreset={deletePreset}
        onApplyPreset={applyPreset}
        compare={compare}
        setCompare={setCompare}
        onExportPdf={exportPdf}
      />

      {/* KPI STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ti-kpi-strip">
        <KpiTile compare={compare} label="Completion rate"        value={fmtPct(kpi("completion_rate_pct"))}       prevValue={fmtPct(kpiPrev("completion_rate_pct"))}       delta={kpiDelta("completion_rate_pct")}       spark={{data: sparkSeries, dataKey: "done", color: "#10b981"}} testid="kpi-completion" />
        <KpiTile compare={compare} label="On-time delivery"        value={fmtPct(kpi("on_time_rate_pct"))}          prevValue={fmtPct(kpiPrev("on_time_rate_pct"))}          delta={kpiDelta("on_time_rate_pct")}          spark={{data: sparkSeries, dataKey: "on_time_pct", color: "#f59e0b"}}  hint={`sample ${kpi("on_time_sample") ?? 0}`} testid="kpi-ontime" />
        <KpiTile compare={compare} label="Overdue"                 value={kpi("overdue_count") ?? "—"}              prevValue={kpiPrev("overdue_count") ?? "—"}              delta={kpiDelta("overdue_count")}             higherIsBetter={false} testid="kpi-overdue" />
        <KpiTile compare={compare} label="Avg cycle (days)"        value={fmtNum(kpi("avg_cycle_days"), 2)}         prevValue={fmtNum(kpiPrev("avg_cycle_days"), 2)}         delta={kpiDelta("avg_cycle_days")}            higherIsBetter={false} testid="kpi-cycle" />
        <KpiTile compare={compare} label="Effort variance"         value={fmtPct(kpi("avg_effort_variance_pct"))}   prevValue={fmtPct(kpiPrev("avg_effort_variance_pct"))}   delta={kpiDelta("avg_effort_variance_pct")}   spark={{data: sparkSeries, dataKey: "variance_pct", color: "#ef4444"}} higherIsBetter={false} testid="kpi-variance" />
        <KpiTile compare={compare} label="Billable utilisation"    value={fmtPct(kpi("billable_utilization_pct"))}  prevValue={fmtPct(kpiPrev("billable_utilization_pct"))}  delta={kpiDelta("billable_utilization_pct")}  hint={`${fmtHrs(kpi("billable_hours"))} / ${fmtHrs(kpi("total_hours"))}`} testid="kpi-billable" />
        <KpiTile compare={compare} label="Quality pass rate"       value={fmtPct(kpi("quality_pass_rate_pct"))}     prevValue={fmtPct(kpiPrev("quality_pass_rate_pct"))}     delta={kpiDelta("quality_pass_rate_pct")}     hint={`rated ${kpi("quality_sample") ?? 0}`} testid="kpi-quality" />
        <KpiTile compare={compare} label="Reopen rate"             value={fmtPct(kpi("reopen_rate_pct"))}           prevValue={fmtPct(kpiPrev("reopen_rate_pct"))}           delta={kpiDelta("reopen_rate_pct")}           higherIsBetter={false} testid="kpi-reopen" />
      </div>

      {/* AI narrative */}
      <div className="border border-border p-5 bg-foreground text-background" data-testid="ti-ai-narrative-card">
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div>
            <div className="label-eyebrow opacity-70 mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3" /> AI intelligence</div>
            <h3 className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>
              {narrative?.narrative?.headline || "Generate an executive summary from the current filters."}
            </h3>
          </div>
          <div className="flex gap-2">
            {["executive", "coach", "blunt"].map((t) => (
              <Button key={t} size="sm" variant="secondary" className="rounded-none h-9 text-xs uppercase" disabled={narrativeBusy} onClick={() => runNarrative(t)} data-testid={`ti-ai-btn-${t}`}>
                {narrativeBusy ? "…" : t}
              </Button>
            ))}
          </div>
        </div>
        {narrative?.narrative && (
          <div className="grid lg:grid-cols-4 gap-3 mt-4">
            <NarrativeCol title="Wins" tone="good" items={narrative.narrative.wins} />
            <NarrativeCol title="Concerns" tone="warn" items={narrative.narrative.concerns} />
            <NarrativeCol title="Watchlist" tone="alert" items={narrative.narrative.person_watchlist} />
            <NarrativeCol title="Recommended actions" tone="info" items={(narrative.narrative.recommended_actions || []).map((a) => ({ title: a }))} />
          </div>
        )}
      </div>

      {/* TREND CHART */}
      <div className="border border-border p-3 sm:p-5" data-testid="ti-trend-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow mb-1">Trend</div>
            <h3 className="text-xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>Created vs Done · On-time% · Variance%</h3>
          </div>
          <div className="flex gap-1">
            {["day", "week", "month"].map((b) => (
              <Button key={b} size="sm" variant={trendsBucket === b ? "default" : "outline"} className="rounded-none h-8 text-xs uppercase" onClick={() => setTrendsBucket(b)} data-testid={`ti-trend-bucket-${b}`}>{b}</Button>
            ))}
          </div>
        </div>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={trendChartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line yAxisId="l" type="monotone" dataKey="created" stroke="#6366f1" strokeWidth={2} dot={false} name="Created" />
              <Line yAxisId="l" type="monotone" dataKey="done"    stroke="#10b981" strokeWidth={2} dot={false} name="Done" />
              <Line yAxisId="r" type="monotone" dataKey="on_time_pct" stroke="#f59e0b" strokeWidth={2} dot={false} name="On-time %" />
              <Line yAxisId="r" type="monotone" dataKey="variance_pct" stroke="#ef4444" strokeWidth={2} dot={false} name="Variance %" />
              {compare && (
                <>
                  <Line yAxisId="l" type="monotone" dataKey="prev_created" stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Created (prev)" />
                  <Line yAxisId="l" type="monotone" dataKey="prev_done"    stroke="#10b981" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Done (prev)" />
                </>
              )}
              <ReferenceLine yAxisId="r" y={0} stroke="#666" opacity={0.4} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* LEADERBOARD (own row) */}
      <div className="border border-border p-3 sm:p-5 overflow-x-auto" data-testid="ti-leaderboard-card">
        <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-primary" /><h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>Team leaderboard</h3></div>
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {["Assignee","Tasks","Done","Compl%","On-time","Variance","Cycle","Hrs","Capacity","Util%","Billable%","Quality",""].map((h) => (
                <th key={h} className="text-left px-2 py-2 label-eyebrow whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(leaderboard?.rows || []).filter((r) => !(filters?.hide_idle && r.is_idle)).map((r) => {
              const util = r.utilization_pct;
              const utilCls = util == null ? "text-muted-foreground"
                : util >= 100 ? "text-red-600 font-semibold"
                : util >= 80 ? "text-amber-600 font-semibold"
                : util >= 50 ? "text-primary"
                : "text-emerald-600";
              const meta = r.period_meta || {};
              const capTitle = r.capacity_hours
                ? `${meta.net_working_days ?? 0} net working days × ${meta.daily_net_hours ?? 0}h/day` +
                  (meta.leave_days > 0 ? ` · -${meta.leave_days}d leave` : "") +
                  (meta.holiday_days > 0 ? ` · -${meta.holiday_days}d holidays` : "")
                : "Capacity unavailable — no employee record or shift";
              return (
                <tr key={r.assignee_id} className={`border-b border-border/50 hover:bg-muted/30 ${r.is_idle ? "bg-muted/20 opacity-75" : ""}`} data-testid={`ti-lb-row-${r.assignee_id}`} data-is-idle={r.is_idle ? "true" : "false"}>
                  <td className="px-2 py-2 font-semibold">
                    <div className="flex items-center gap-1.5">
                      <PersonChip userId={r.assignee_id} name={r.assignee_name} testid={`ti-lb-person-${r.assignee_id}`} />
                      {r.is_idle && (
                        <span className="text-[8px] uppercase tracking-widest px-1 py-0.5 border border-muted-foreground text-muted-foreground bg-background" title="No tasks in this window">
                          Idle
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">{r.tasks_total}</td>
                  <td className="px-2 py-2">{r.tasks_done}</td>
                  <td className="px-2 py-2">{fmtPct(r.completion_rate_pct)}</td>
                  <td className="px-2 py-2">{fmtPct(r.on_time_pct)}</td>
                  <td className={`px-2 py-2 ${varianceCls(r.avg_variance_pct)}`}>{fmtPct(r.avg_variance_pct)}</td>
                  <td className="px-2 py-2">{fmtNum(r.avg_cycle_days, 1)}d</td>
                  <td className="px-2 py-2">{fmtHrs(r.total_hours)}</td>
                  <td className="px-2 py-2" title={capTitle} data-testid={`ti-lb-capacity-${r.assignee_id}`}>{r.capacity_hours ? fmtHrs(r.capacity_hours) : "—"}</td>
                  <td className={`px-2 py-2 ${utilCls}`} data-testid={`ti-lb-util-${r.assignee_id}`}>{util != null ? fmtPct(util) : "—"}</td>
                  <td className="px-2 py-2">{fmtPct(r.billable_utilization_pct)}</td>
                  <td className="px-2 py-2">{fmtPct(r.quality_pass_rate_pct)}</td>
                  <td className="px-2 py-2 text-right">
                    <Button size="sm" variant="ghost" className="h-7 rounded-none text-[11px]"
                            onClick={() => openDrill(r.assignee_name, r.assignee_id)}
                            data-testid={`ti-lb-drill-${r.assignee_id}`}>
                      Inspect →
                    </Button>
                  </td>
                </tr>
              );
            })}
            {(!leaderboard || leaderboard.rows?.length === 0) && (
              <tr><td colSpan={13} className="px-2 py-4 text-center text-muted-foreground">No data in this range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* FREE/BUSY (own row) */}
      <div className="border border-border p-3 sm:p-5" data-testid="ti-freebusy-card">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-primary" />
          <h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>Free / busy</h3>
          {freeBusy?.capacity_mode === "period" && (
            <Badge variant="outline" className="rounded-none text-[9px] uppercase tracking-widest ml-2">
              Period · {freeBusy?.period?.from} → {freeBusy?.period?.to}
            </Badge>
          )}
          {compare && prevFreeBusy?.period && (
            <Badge variant="outline" className="rounded-none text-[9px] uppercase tracking-widest border-violet-500/60 text-violet-700">
              vs · {prevFreeBusy.period.from} → {prevFreeBusy.period.to}
            </Badge>
          )}
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] uppercase tracking-widest ml-auto sm:ml-0"
                  onClick={() => setShowCapacityOverrides(true)}
                  data-testid="ti-fb-overrides-btn">
            Overrides
          </Button>
          <div className="text-[10px] text-muted-foreground hidden sm:block">
            Capacity respects each employee&apos;s shift, joining/exit, holidays &amp; leaves
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ti-fb-grid">
          {(freeBusy?.rows || []).filter((r) => !(filters?.hide_idle && r.is_idle)).map((r) => {
            const pct = r.utilization_pct || 0;
            const cls = pct >= 100 ? "bg-destructive" : pct >= 80 ? "bg-amber-500" : pct >= 50 ? "bg-primary" : "bg-emerald-500";
            const cap = Number(r.capacity_hours || 0);
            const est = Number(r.estimate_hours || 0);
            const log = Number(r.logged_hours || 0);
            const denom = Math.max(cap, est, log, 1);
            const logPct = Math.min((log / denom) * 100, 100);
            const estPct = Math.min((est / denom) * 100, 100);
            const capPct = Math.min((cap / denom) * 100, 100);
            const overCapacity = est > cap && cap > 0;
            const overEstimate = log > est && est > 0;
            // R138ak — Team Load Compare: delta vs previous period
            const prevRow = compare && prevFreeBusy?.rows
              ? prevFreeBusy.rows.find((p) => p.user_id === r.user_id) : null;
            const utilDelta = prevRow != null && prevRow.utilization_pct != null && r.utilization_pct != null
              ? Math.round((r.utilization_pct - prevRow.utilization_pct) * 10) / 10 : null;
            // R138an — Capacity Wall Planner: drop target
            const onDragOver = (e) => {
              const from = e.dataTransfer.types.includes("text/x-task-from");
              if (from) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }
            };
            const onDrop = async (e) => {
              e.preventDefault();
              const taskId = e.dataTransfer.getData("text/x-task-id");
              const taskTitle = e.dataTransfer.getData("text/x-task-title");
              const fromUid = e.dataTransfer.getData("text/x-task-from");
              const taskHoursStr = e.dataTransfer.getData("text/x-task-hours");
              const taskHours = Number(taskHoursStr) || 0;
              if (!taskId || fromUid === r.user_id) return;
              // R138ao — Rebalance Preview: compute projected utilisation
              // for both source and target before confirming.
              const fromRow = (freeBusy?.rows || []).find((x) => x.user_id === fromUid);
              const fromName = fromRow?.user_name || fromUid;
              const fromCap = fromRow?.capacity_hours || 0;
              const fromPlanned = fromRow?.planned_hours || 0;
              const fromNewPlanned = Math.max(fromPlanned - taskHours, 0);
              const fromNewPct = fromCap > 0 ? Math.round((fromNewPlanned / fromCap) * 100) : null;
              const toCap = r.capacity_hours || 0;
              const toPlanned = r.planned_hours || 0;
              const toNewPlanned = toPlanned + taskHours;
              const toNewPct = toCap > 0 ? Math.round((toNewPlanned / toCap) * 100) : null;
              const fromPctNow = fromRow?.utilization_pct?.toFixed?.(0);
              const toPctNow = r.utilization_pct?.toFixed?.(0);
              const preview =
                `Reassign "${taskTitle}" (${taskHours}h)?\n\n` +
                `From ${fromName}\n` +
                `  Load: ${fromPctNow ?? "—"}%${fromNewPct != null ? ` → ${fromNewPct}%` : ""}\n` +
                `  Hours: ${fromPlanned.toFixed(1)}h → ${fromNewPlanned.toFixed(1)}h / ${fromCap.toFixed(0)}h cap\n\n` +
                `To ${r.user_name}\n` +
                `  Load: ${toPctNow ?? "—"}%${toNewPct != null ? ` → ${toNewPct}%` : ""}${toNewPct != null && toNewPct >= 100 ? " ⚠ OVERLOADED" : ""}\n` +
                `  Hours: ${toPlanned.toFixed(1)}h → ${toNewPlanned.toFixed(1)}h / ${toCap.toFixed(0)}h cap`;
              if (!window.confirm(preview)) return;
              try {
                await api.patch(`/tasks/${taskId}`, { assignee_id: r.user_id });
                toast.success(`Reassigned to ${r.user_name}${toNewPct != null && toNewPct >= 100 ? " · they're now over capacity" : ""}`);
                load();
              } catch (err) {
                toast.error(apiErrorMessage(err, "Reassign failed"));
              }
            };
            return (
              <div key={r.user_id}
                   onDragOver={onDragOver} onDrop={onDrop}
                   className={`border border-border p-3 hover:border-primary/40 group ${r.is_idle ? "bg-muted/20 opacity-80" : ""}`}
                   data-testid={`ti-fb-row-${r.user_id}`}
                   data-is-idle={r.is_idle ? "true" : "false"}>
                <button type="button"
                        onClick={() => openDrill(r.user_name, r.user_id)}
                        className="w-full text-left">
                <div className="flex justify-between items-baseline gap-2">
                  <div className="flex items-center gap-1.5">
                    <PersonChip userId={r.user_id} name={r.user_name} className="font-semibold text-sm" testid={`ti-fb-person-${r.user_id}`} />
                    {r.is_idle && (
                      <span className="text-[8px] uppercase tracking-widest px-1 py-0.5 border border-muted-foreground text-muted-foreground bg-background" title="No tasks in this window">
                        Idle
                      </span>
                    )}
                  </div>                  <div className="flex items-center gap-1">
                    {utilDelta != null && (
                      <span
                        className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                          utilDelta > 5 ? "border-red-500/50 text-red-600 bg-red-500/10"
                          : utilDelta < -5 ? "border-emerald-500/50 text-emerald-700 bg-emerald-500/10"
                          : "border-border text-muted-foreground bg-muted/50"
                        }`}
                        title={`Previous period utilisation: ${prevRow?.utilization_pct?.toFixed?.(1) ?? "—"}%`}
                        data-testid={`ti-fb-delta-${r.user_id}`}>
                        {utilDelta > 0 ? "▲" : utilDelta < 0 ? "▼" : "="} {Math.abs(utilDelta)}pt
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">{fmtHrs(r.planned_hours)} / {fmtHrs(r.capacity_hours)}</span>
                  </div>
                </div>
                <div className="h-2 mt-2 bg-muted overflow-hidden">
                  <div className={cls} style={{ width: `${Math.min(pct, 100)}%`, height: "100%" }} />
                </div>
                {/* Logged vs Estimate vs Capacity — stacked mini-bars */}
                <div className="mt-2 space-y-1" data-testid={`ti-fb-lec-${r.user_id}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-14 text-[9px] uppercase tracking-widest text-muted-foreground">Logged</span>
                    <div className="flex-1 h-1.5 bg-muted overflow-hidden">
                      <div className={overEstimate ? "bg-red-500" : "bg-primary"} style={{ width: `${logPct}%`, height: "100%" }} />
                    </div>
                    <span className={`font-mono text-[10px] w-12 text-right ${overEstimate ? "text-red-600 font-semibold" : ""}`} data-testid={`ti-fb-logged-${r.user_id}`}>{fmtHrs(log)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-14 text-[9px] uppercase tracking-widest text-muted-foreground">Estimate</span>
                    <div className="flex-1 h-1.5 bg-muted overflow-hidden">
                      <div className={overCapacity ? "bg-amber-500" : "bg-violet-500"} style={{ width: `${estPct}%`, height: "100%" }} />
                    </div>
                    <span className={`font-mono text-[10px] w-12 text-right ${overCapacity ? "text-amber-600 font-semibold" : ""}`} data-testid={`ti-fb-estimate-${r.user_id}`}>{fmtHrs(est)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-14 text-[9px] uppercase tracking-widest text-muted-foreground">Capacity</span>
                    <div className="flex-1 h-1.5 bg-muted overflow-hidden">
                      <div className="bg-emerald-500/70" style={{ width: `${capPct}%`, height: "100%" }} />
                    </div>
                    <span className="font-mono text-[10px] w-12 text-right" data-testid={`ti-fb-capacity-${r.user_id}`}>{fmtHrs(cap)}</span>
                  </div>
                </div>
                <div className="text-[10px] mt-2 text-muted-foreground uppercase tracking-wider">{r.status} · {r.task_count} tasks{r.urgent_task_count ? ` · ${r.urgent_task_count} urgent` : ""}</div>
                {/* R138ag — Period capacity breakdown chip. Shows why
                    this person's capacity is what it is (working days
                    net of leaves/holidays, shift hours, joining/exit
                    clamps). Only rendered when the backend returned
                    period-aware data. */}
                {r.period_meta && (
                  <div className="mt-1.5 flex flex-wrap gap-1 text-[9px] uppercase tracking-widest"
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`ti-fb-period-meta-${r.user_id}`}>
                    <span className="px-1.5 py-[1px] bg-muted/60 border border-border/60">
                      {r.period_meta.net_working_days ?? 0}d · {r.period_meta.daily_net_hours ?? 0}h/day
                    </span>
                    {r.period_meta.leave_days > 0 && (
                      <span className="px-1.5 py-[1px] bg-amber-500/10 border border-amber-500/40 text-amber-700"
                            title={`Approved leaves in this period reduce capacity`}>
                        -{r.period_meta.leave_days}d leave
                      </span>
                    )}
                    {r.period_meta.holiday_days > 0 && (
                      <span className="px-1.5 py-[1px] bg-sky-500/10 border border-sky-500/40 text-sky-700"
                            title={`Public holidays in this period`}>
                        {r.period_meta.holiday_days}d holidays
                      </span>
                    )}
                    {(r.period_meta.joining_date && r.period_meta.effective_from &&
                        r.period_meta.effective_from === r.period_meta.joining_date) && (
                      <span className="px-1.5 py-[1px] bg-emerald-500/10 border border-emerald-500/40 text-emerald-700"
                            title={`Joined ${r.period_meta.joining_date}`}>
                        joined {r.period_meta.joining_date}
                      </span>
                    )}
                    {(r.period_meta.exit_date && r.period_meta.effective_to &&
                        r.period_meta.effective_to === r.period_meta.exit_date) && (
                      <span className="px-1.5 py-[1px] bg-rose-500/10 border border-rose-500/40 text-rose-700"
                            title={`Exit ${r.period_meta.exit_date}`}>
                        exit {r.period_meta.exit_date}
                      </span>
                    )}
                    {r.period_meta.override_days > 0 && (
                      <span className="px-1.5 py-[1px] bg-violet-500/10 border border-violet-500/40 text-violet-700"
                            title={`${r.period_meta.override_days} day(s) covered by capacity overrides (${r.period_meta.override_hours}h)`}>
                        {r.period_meta.override_days}d override
                      </span>
                    )}
                  </div>
                )}
                {/* 14-day heatmap strip — hover any cell to see date + hours */}
                {Array.isArray(r.daily) && r.daily.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50" data-testid={`ti-fb-heatmap-${r.user_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Next {r.daily.length}d · hover a day</span>
                      <span className="text-[9px] text-muted-foreground">
                        {(() => { const peak = r.daily.reduce((a, b) => (b.planned_hours > a.planned_hours ? b : a), r.daily[0]);
                          return peak.planned_hours > 0 ? `peak ${fmtHrs(peak.planned_hours)} · ${peak.date.slice(5)}` : "no load"; })()}
                      </span>
                    </div>
                    <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                      {r.daily.map((cell) => {
                        const lp = cell.load_pct || 0;
                        // Colour ramp: weekend = neutral stripe, else load buckets.
                        const bg = cell.is_weekend
                          ? "bg-muted/30 border-dashed"
                          : lp >= 120 ? "bg-red-600"
                          : lp >= 100 ? "bg-red-500"
                          : lp >= 80  ? "bg-amber-500"
                          : lp >= 50  ? "bg-primary"
                          : lp >= 20  ? "bg-emerald-500"
                          : lp > 0    ? "bg-emerald-500/40"
                          :             "bg-muted";
                        const dow = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][cell.weekday];
                        const titles = (cell.task_titles || []).slice(0, 6);
                        const more = Math.max((cell.tasks || 0) - titles.length, 0);
                        const titleList = titles.length
                          ? "\n\nTasks:\n • " + titles.map((tt) => tt.length > 60 ? tt.slice(0, 57) + "…" : tt).join("\n • ")
                            + (more > 0 ? `\n • +${more} more…` : "")
                          : "";
                        const title = cell.is_weekend
                          ? `${dow} ${cell.date} — weekend (${fmtHrs(cell.planned_hours)} carried over)${titleList}`
                          : `${dow} ${cell.date}\n${fmtHrs(cell.planned_hours)} planned · ${fmtHrs(cell.logged_hours)} logged · ${fmtHrs(cell.estimate_hours)} est\n${cell.tasks} task${cell.tasks === 1 ? "" : "s"} · ${lp}% of ${fmtHrs(cell.capacity_hours)} capacity${titleList}`;
                        const hasTasks = (cell.tasks || 0) > 0 && (cell.task_ids || []).length > 0;
                        return (
                          <div
                            key={cell.date}
                            role={hasTasks ? "button" : undefined}
                            tabIndex={hasTasks ? 0 : -1}
                            onClick={hasTasks ? (e) => {
                              e.stopPropagation();
                              openTaskIdsDrill(`${r.user_name} · ${cell.date}`, r.user_id, cell.task_ids);
                            } : undefined}
                            className={`flex-1 h-6 border border-border/50 ${bg} ${hasTasks ? "cursor-pointer hover:ring-2 hover:ring-primary hover:ring-offset-1 hover:ring-offset-background" : "cursor-help"} transition-shadow`}
                            title={title}
                            data-testid={`ti-fb-heatmap-cell-${r.user_id}-${cell.date}`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[8px] text-muted-foreground mt-0.5 font-mono">
                      <span>{r.daily[0]?.date.slice(5)}</span>
                      <span>{r.daily[r.daily.length - 1]?.date.slice(5)}</span>
                    </div>
                  </div>
                )}
                </button>
                {/* R138an — Capacity Wall Planner: draggable task chips */}
                {Array.isArray(r.top_tasks) && r.top_tasks.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-border/60"
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`ti-fb-planner-${r.user_id}`}>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-[9px] uppercase tracking-widest text-muted-foreground flex-1">
                        Drag to reassign ({r.top_tasks.length} of {r.task_count})
                      </span>
                      <span className="text-[9px] text-muted-foreground opacity-60 hidden sm:inline">✥</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {r.top_tasks.map((tk) => (
                        <div key={tk.id}
                             draggable
                             onDragStart={(e) => {
                               e.stopPropagation();
                               e.dataTransfer.effectAllowed = "move";
                               e.dataTransfer.setData("text/x-task-id", tk.id);
                               e.dataTransfer.setData("text/x-task-title", tk.title);
                               e.dataTransfer.setData("text/x-task-from", r.user_id);
                               e.dataTransfer.setData("text/x-task-hours", String(tk.remaining_hours || tk.est_hours || 0));
                             }}
                             title={`${tk.title}${tk.due_date ? ` · due ${tk.due_date}` : ""}${tk.remaining_hours ? ` · ${tk.remaining_hours}h left` : ""}${tk.priority === "urgent" ? " · URGENT" : ""}\n\nDrag onto another person's card to reassign.`}
                             className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] cursor-grab active:cursor-grabbing hover:border-primary hover:bg-primary/5 max-w-[180px] ${
                               tk.priority === "urgent" ? "border-red-500/50 bg-red-500/5 text-red-700" : "border-border bg-background"
                             }`}
                             data-testid={`ti-fb-task-chip-${tk.id}`}>
                          <span className="truncate">{tk.title}</span>
                          <span className="font-mono opacity-60">{tk.remaining_hours || tk.est_hours}h</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {(!freeBusy || freeBusy.rows?.length === 0) && <div className="text-sm text-muted-foreground col-span-full">No forward-loaded tasks in this period.</div>}
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground uppercase tracking-widest hidden sm:block">
          Tip: drag a task chip from one person&apos;s card onto another to reassign in place.
        </div>
      </div>

      {/* VARIANCE MATRIX */}
      <div className="border border-border p-5 overflow-x-auto" data-testid="ti-matrix-card">
        <div className="flex items-center gap-2 mb-4"><Layers className="w-4 h-4 text-primary" /><h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>Effort variance heatmap · projects × people</h3></div>
        {matrix && matrix.rows?.length > 0 ? (
          <table className="text-xs">
            <thead>
              <tr>
                <th className="text-left px-2 py-2 label-eyebrow whitespace-nowrap">Project</th>
                {matrix.columns?.map((c) => (
                  <th key={c.user_id} className="text-left px-2 py-2 label-eyebrow whitespace-nowrap min-w-[90px]"
                      title={c.capacity_hours ? `${c.capacity_hours}h capacity · ${c.net_working_days ?? 0} net working days` : ""}>
                    {c.user_name}
                    {c.capacity_hours > 0 && (
                      <div className="text-[9px] font-normal text-muted-foreground normal-case tracking-normal">
                        cap {c.capacity_hours}h
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((r) => (
                <tr key={r.project_id} className="border-b border-border/50">
                  <td className="px-2 py-2 font-semibold whitespace-nowrap">{r.project_name}</td>
                  {r.cells.map((cell, ci) => {
                    if (!cell.task_count) return <td key={ci} className="px-2 py-2 text-muted-foreground/40">·</td>;
                    const v = cell.variance_pct;
                    const bg = v == null ? "bg-muted/30"
                             : v <= -10 ? "bg-emerald-100 dark:bg-emerald-900/30"
                             : v <= 10  ? "bg-muted/40"
                             : v <= 30  ? "bg-amber-100 dark:bg-amber-900/30"
                             : "bg-red-100 dark:bg-red-900/30";
                    return (
                      <td key={ci} className={`px-2 py-2 ${bg} cursor-pointer hover:opacity-70`}
                          onClick={() => openDrill(cell.user_name, cell.user_id)}
                          title={`${cell.task_count} tasks · est ${cell.est_hours}h · act ${cell.act_hours}h`}
                          data-testid={`ti-matrix-cell-${r.project_id}-${cell.user_id}`}>
                        <div className={`font-semibold ${varianceCls(v)}`}>{fmtPct(v)}</div>
                        <div className="text-[10px] text-muted-foreground">{cell.task_count} · {cell.act_hours}h</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-sm text-muted-foreground">Not enough completed tasks with estimates + actuals in this range to build a heatmap.</div>
        )}
      </div>

      {/* TIMELINESS + OVERRUN */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* TIMELINESS */}
        <div className="border border-border p-3 sm:p-5" data-testid="ti-timeliness-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /><h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>Timeliness</h3></div>
            <Tabs value={timelinessBy} onValueChange={setTimelinessBy}>
              <TabsList className="h-8">
                <TabsTrigger value="assignee" className="text-xs" data-testid="ti-timeliness-by-assignee">By person</TabsTrigger>
                <TabsTrigger value="project"  className="text-xs" data-testid="ti-timeliness-by-project">By project</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={timeliness?.rows?.slice(0, 12) || []} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={110} />
                <Tooltip />
                <Legend />
                <Bar dataKey="on_time"             stackId="a" fill="#10b981" name="On time" />
                <Bar dataKey="late_1_3"            stackId="a" fill="#84cc16" name="1-3d late" />
                <Bar dataKey="late_4_7"            stackId="a" fill="#f59e0b" name="4-7d late" />
                <Bar dataKey="late_7_plus"         stackId="a" fill="#ef4444" name="7d+ late" />
                <Bar dataKey="still_open_past_due" stackId="a" fill="#a3a3a3" name="Open past due" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* OVERRUN */}
        <div className="border border-border p-3 sm:p-5" data-testid="ti-overrun-card">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-primary" /><h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>Overrun attribution</h3></div>
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {["Person","Tasks","Over","Over-hrs","Over-rate"].map((h) => <th key={h} className="text-left px-2 py-2 label-eyebrow">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {(overrun?.rows || []).slice(0, 10).map((r) => (
                <tr key={r.user_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer"
                    onClick={() => openDrill(r.user_name, r.user_id)}
                    data-testid={`ti-ov-row-${r.user_id}`}>
                  <td className="px-2 py-2 font-semibold">
                    <PersonChip userId={r.user_id} name={r.user_name} testid={`ti-ov-person-${r.user_id}`} />
                  </td>
                  <td className="px-2 py-2">{r.tasks_considered}</td>
                  <td className="px-2 py-2">{r.tasks_over}</td>
                  <td className={`px-2 py-2 font-semibold ${r.over_hours > 20 ? "text-destructive" : r.over_hours > 5 ? "text-amber-600" : "text-foreground/80"}`}>+{fmtHrs(r.over_hours)}</td>
                  <td className="px-2 py-2">{fmtPct(r.over_rate_pct)}</td>
                </tr>
              ))}
              {(!overrun?.rows?.length) && <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">No overruns in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATORS — who plans / replans / manages projects */}
      <div className="border border-border p-3 sm:p-5 overflow-x-auto" data-testid="ti-creators-card">
        <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-primary" /><h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>Task creators &middot; who&apos;s planning &amp; replanning</h3></div>
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {["Person","Created","Projects","Urgent","Avg est","Compl%","On-time%","Variance","Quality","Replans","Distinct tasks replanned",""].map((h) => (
                <th key={h} className="text-left px-2 py-2 label-eyebrow whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(creators?.rows || []).map((r) => (
              <tr key={r.user_id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`ti-cr-row-${r.user_id}`}>
                <td className="px-2 py-2 font-semibold">
                  <PersonChip userId={r.user_id} name={r.user_name} testid={`ti-cr-person-${r.user_id}`} />
                </td>
                <td className="px-2 py-2 font-semibold">{r.tasks_created}</td>
                <td className="px-2 py-2">{r.projects_span}</td>
                <td className="px-2 py-2">{r.urgent_created}</td>
                <td className="px-2 py-2">{fmtHrs(r.avg_est_hours)}</td>
                <td className="px-2 py-2">{fmtPct(r.completion_pct_of_own_created)}</td>
                <td className="px-2 py-2">{fmtPct(r.on_time_pct_of_own_created)}</td>
                <td className={`px-2 py-2 ${varianceCls(r.avg_variance_pct_own_created)}`}>{fmtPct(r.avg_variance_pct_own_created)}</td>
                <td className="px-2 py-2">{fmtPct(r.quality_pass_pct_own_created)}</td>
                <td className={`px-2 py-2 ${r.replans > 20 ? "text-amber-600 font-semibold" : "text-foreground/80"}`}>
                  {r.replans > 0 ? (
                    <button className="hover:underline"
                            onClick={() => openTaskIdsDrill(r.user_name, r.user_id, r.replanned_task_ids || [])}
                            data-testid={`ti-cr-replans-${r.user_id}`}>
                      {r.replans} →
                    </button>
                  ) : r.replans}
                </td>
                <td className="px-2 py-2">{r.distinct_replanned_tasks}</td>
                <td className="px-2 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-7 rounded-none text-[11px]"
                          onClick={() => openDrill(r.user_name, r.user_id)}
                          data-testid={`ti-cr-drill-${r.user_id}`}>
                    Inspect →
                  </Button>
                </td>
              </tr>
            ))}
            {(!creators?.rows || creators.rows.length === 0) && (
              <tr><td colSpan={12} className="px-2 py-4 text-center text-muted-foreground">No task creations in this range.</td></tr>
            )}
          </tbody>
        </table>
        <div className="text-[11px] text-muted-foreground mt-3">
          Replans counted via audit_log entries changing <code>due_date</code> or <code>start_date</code> on the creator&apos;s tasks. A high replan count with low on-time% indicates weak initial planning.
        </div>
      </div>

      {/* COSTLY / PROFITABLE / UNPROFITABLE + PROJECT ECONOMICS */}
      <div className="border border-border p-3 sm:p-5" data-testid="ti-cp-card">
        <div className="flex items-center gap-2 mb-4"><DollarSign className="w-4 h-4 text-primary" /><h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>Economics &middot; task &amp; project level</h3></div>
        {costly?.note && <div className="text-[11px] text-muted-foreground mb-3">{costly.note}</div>}
        <Tabs defaultValue="costly">
          <TabsList>
            <TabsTrigger value="costly"       data-testid="ti-cp-tab-costly">Costliest tasks</TabsTrigger>
            <TabsTrigger value="profitable"   data-testid="ti-cp-tab-profitable">Most profitable tasks</TabsTrigger>
            <TabsTrigger value="unprofitable" data-testid="ti-cp-tab-unprofitable">Unprofitable tasks</TabsTrigger>
            <TabsTrigger value="projects"     data-testid="ti-cp-tab-projects">By project</TabsTrigger>
          </TabsList>
          {[
            ["costly", costly?.costly, "cost"],
            ["profitable", costly?.profitable, "profit"],
            ["unprofitable", costly?.unprofitable, "profit"],
          ].map(([key, list, sortBy]) => (
            <TabsContent key={key} value={key}>
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {["Task","Project","Assignee","Logged","Est","Overrun","Cost","Revenue","Profit"].map((h) => <th key={h} className="text-left px-2 py-2 label-eyebrow whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(list || []).map((t) => (
                    <tr key={t.id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer"
                        onClick={() => openEdit(t, () => load())}
                        data-testid={`ti-cp-row-${key}-${t.id}`}>
                      <td className="px-2 py-2 font-semibold max-w-[280px] truncate">{t.title || "(untitled)"}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{t.project_name}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{t.assignee_name}</td>
                      <td className="px-2 py-2">{fmtHrs(t.logged_hours)}</td>
                      <td className="px-2 py-2">{fmtHrs(t.est_hours)}</td>
                      <td className={`px-2 py-2 ${t.overrun_h > 5 ? "text-destructive font-semibold" : "text-foreground/80"}`}>{t.overrun_h > 0 ? `+${fmtHrs(t.overrun_h)}` : fmtHrs(t.overrun_h)}</td>
                      <td className="px-2 py-2">{t.cost?.toFixed?.(0) ?? t.cost ?? "—"}</td>
                      <td className="px-2 py-2">{t.revenue?.toFixed?.(0) ?? t.revenue ?? "—"}</td>
                      <td className={`px-2 py-2 font-semibold ${t.profit >= 0 ? "text-emerald-600" : "text-destructive"}`}>{t.profit?.toFixed?.(0) ?? t.profit ?? "—"}</td>
                    </tr>
                  ))}
                  {(!list || !list.length) && <tr><td colSpan={9} className="px-2 py-4 text-center text-muted-foreground">No tasks in this bucket.</td></tr>}
                </tbody>
              </table>
            </TabsContent>
          ))}
          <TabsContent value="projects">
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs" data-testid="ti-pe-table">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {["Project","Tasks","Done","Compl%","On-time","Est","Actual","Variance","Cost","Revenue","Profit","Margin"].map((h) => <th key={h} className="text-left px-2 py-2 label-eyebrow whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(projectEconomics?.rows || []).map((p) => (
                    <tr key={p.project_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer" onClick={() => openProjectDrill(p.project_name, p.project_id)} data-testid={`ti-pe-row-${p.project_id}`}>
                      <td className="px-2 py-2 font-semibold max-w-[240px] truncate">
                        {p.project_id && p.project_id !== "no-project" ? (
                          <a href={`/app/projects/${p.project_id}`} onClick={(e) => e.stopPropagation()} className="hover:underline text-primary">{p.project_name}</a>
                        ) : p.project_name}
                      </td>
                      <td className="px-2 py-2">{p.tasks}</td>
                      <td className="px-2 py-2">{p.done}</td>
                      <td className="px-2 py-2">{fmtPct(p.completion_rate_pct)}</td>
                      <td className="px-2 py-2">{fmtPct(p.on_time_pct)}</td>
                      <td className="px-2 py-2">{fmtHrs(p.est_hours)}</td>
                      <td className="px-2 py-2">{fmtHrs(p.actual_hours)}</td>
                      <td className={`px-2 py-2 ${varianceCls(p.variance_pct)}`}>{fmtPct(p.variance_pct)}</td>
                      <td className="px-2 py-2">{p.cost?.toFixed?.(0) ?? "—"}</td>
                      <td className="px-2 py-2">{p.revenue?.toFixed?.(0) ?? "—"}</td>
                      <td className={`px-2 py-2 font-semibold ${p.profit >= 0 ? "text-emerald-600" : "text-destructive"}`}>{p.profit?.toFixed?.(0) ?? "—"}</td>
                      <td className="px-2 py-2">{fmtPct(p.margin_pct)}</td>
                    </tr>
                  ))}
                  {(!projectEconomics?.rows || projectEconomics.rows.length === 0) && (
                    <tr><td colSpan={12} className="px-2 py-4 text-center text-muted-foreground">No projects in this range.</td></tr>
                  )}
                </tbody>
              </table>
              {projectEconomics?.note && <div className="text-[11px] text-muted-foreground mt-3">{projectEconomics.note}</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <PersonTasksDialog
        open={drill.open}
        onClose={() => setDrill({ open: false, personName: "", personId: null, projectId: null, projectName: "", taskIds: null })}
        personName={drill.personName}
        personId={drill.personId}
        projectId={drill.projectId}
        projectName={drill.projectName}
        taskIds={drill.taskIds}
      />
      {editTask.open && editTask.task && (
        <TaskDetail
          task={editTask.task}
          members={rawMembers}
          allTasks={[]}
          onClose={() => setEditTask({ open: false, task: null, onSaved: null })}
          onPatch={patchEditingTask}
          onArchiveChanged={() => { load(); setEditTask({ open: false, task: null, onSaved: null }); }}
        />
      )}
      {showCapacityOverrides && (
        <CapacityOverridesDialog
          wsId={ws}
          members={rawMembers}
          onClose={() => setShowCapacityOverrides(false)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function NarrativeCol({ title, items, tone }) {
  const toneCls = {
    good:  "border-emerald-500/40",
    warn:  "border-amber-500/40",
    alert: "border-red-500/40",
    info:  "border-primary/40",
  }[tone] || "border-border";
  const iconCls = {
    good: <TrendingUp className="w-3 h-3 text-emerald-400" />,
    warn: <AlertTriangle className="w-3 h-3 text-amber-400" />,
    alert: <TrendingDown className="w-3 h-3 text-red-400" />,
    info: <Sparkles className="w-3 h-3 text-primary" />,
  }[tone];
  return (
    <div className={`border ${toneCls} p-3 bg-black/20`}>
      <div className="label-eyebrow opacity-70 mb-2 flex items-center gap-1.5 text-[10px]">{iconCls}{title}</div>
      <ul className="space-y-2 text-xs">
        {(items || []).map((it, i) => (
          <li key={i} className="border-b border-white/10 pb-1.5">
            <div className="font-semibold">{it.title || it.name}</div>
            {it.detail && <div className="opacity-70 mt-0.5">{it.detail}</div>}
            {it.note && <div className="opacity-70 mt-0.5">{it.note}</div>}
          </li>
        ))}
        {!(items && items.length) && <li className="opacity-60">—</li>}
      </ul>
    </div>
  );
}


